import { ApplicationRef, Component, Inject, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { AnalyticsService } from './services/analytics.service';
import { SolanaInitialisationService } from './services/sol-services/solana-initialise.service';
import { faqItems, getBrowserName, getCountryFromTimeZone, getParam, getReferrer, handleLastClickTracking } from './shared/constants/app-enums';
import { allLanguages } from './shared/constants/languages';
import { StripePaymentStatusComponent } from './modals/stripe-payment-status/stripe-payment-status.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

declare global {
  interface Window {
    dataLayer: { [key: string]: string | number | boolean | undefined }[];
    VWO: any;
  }
}
export var selectedLang: string;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  canRender: boolean = true;
  base = environment.domain;
  hasStripePaymentStatus?: string | null;
  modalRef!: BsModalRef<StripePaymentStatusComponent>;

  constructor(
    @Inject(DOCUMENT) private dom: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private meta: Meta,
    private title: Title,
    private translate: TranslateService,
    private router: Router,
    private modalService: BsModalService,
    private analyticsService: AnalyticsService
  ) {
    inject(ApplicationRef).isStable.subscribe((isStable) => (this.canRender = isStable || this.canRender));

    handleLastClickTracking(['clickId', 'source', 'tid', 'pid']);
    getReferrer();

    if (typeof window !== 'undefined') {
      const browserType = getBrowserName();
      const userCountry = getCountryFromTimeZone();
      this.analyticsService.vwoData('pageLand', null, false);
      this.analyticsService.vwoData('browserTypeAndUserCountry', { browserType, userCountry });
      let urlParams = new URLSearchParams(document.location.search);

      this.hasStripePaymentStatus = urlParams.get("stripe-payment");
      if (this.hasStripePaymentStatus) {
        const session_id = urlParams.get("session_id");
        if (!!session_id) {
          this.modalRef = this.modalService.show(StripePaymentStatusComponent, {
            class: 'modal-min modal-dialog-centered',
            backdrop: 'static',
            initialState: { session_id: session_id }
          });
        }
      }
    }

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe({
      next: (event: NavigationEnd) => {
        this.updateSEOData(true);
      }
    })

    this.translate.onLangChange.subscribe((params: LangChangeEvent) => {
      selectedLang = params.lang;
      const langObj = allLanguages.filter((x) => x.value === selectedLang)[0];
      this.updateSEOData(false);
      this.dom.documentElement.lang = langObj.hreflang; //making html lang dynamic for SEO
    });
  }

  ngOnInit(): void {
    this.updateSocialCards();
  }

  /**
   * Handled SEO Related Updates i.e. MetaData, Canonical and HrefLangs
   * @param isRouteChange - HrefLangs needs to be updated incase of route change, hence we use this
  */
  updateSEOData(isRouteChange: boolean) {
    this.updateMetaData();
    this.updateCanonical();
    this.updateSocialCards();
    this.addFaqSchema();
    if (isRouteChange) {
      // THIS NEEDS TO BE CALLED ONLY WHEN ROUTE CHANGES i.e. HOMEPAGE TO STAKING or STAKING TO HOMEPAGE
      this.updateHrefLangs();
      this.updateHiddenUrls();
    }
  }

  updateMetaData() {
    const metaTitle = this.translate.instant('misc.meta_title');
    const metaData = this.translate.instant('misc.meta_desc');
    this.title.setTitle(metaTitle);
    this.meta.updateTag({ name: 'description', content: metaData });
    this.meta.updateTag({ name: 'robots', content: environment.robots });
  }

  updateCanonical() {
    const link: HTMLLinkElement | null = this.dom.querySelector('link[rel=canonical]');
    let hrefValue = `${this.base}`;

    if (selectedLang !== 'en') {
      hrefValue += `/${selectedLang}`
    }

    if (link) {
      link.href = hrefValue;
    } else {
      const newLink = this.dom.createElement('link');
      newLink.rel = 'canonical';
      newLink.href = hrefValue;
      this.dom.head.appendChild(newLink);
    }
  }

  updateHrefLangs() {
    const existingLinks = this.dom.querySelectorAll('link[rel="alternate"]');
    existingLinks.forEach((link: Element) => link.remove());
    for (let lang of allLanguages) {
      const newLink = this.dom.createElement('link');
      newLink.rel = 'alternate';
      newLink.hreflang = lang.hreflang;
      let hrefValue = `${this.base}`;

      if (lang.value !== 'en') {
        hrefValue += `/${lang.value}`
      }

      newLink.href = hrefValue;
      this.dom.head.appendChild(newLink);
    }
    const newLink = this.dom.createElement('link');
    newLink.rel = 'alternate';
    newLink.hreflang = 'x-default';
    newLink.href = `${this.base}`;
    this.dom.head.appendChild(newLink);
  }

  addFaqSchema() {
    const faqLists = [...faqItems];
    const keys: string[] = faqLists.flatMap((faq) => {
      return [faq.heading, faq.content]; // no spread needed
    });

    this.translate.get(keys).subscribe((translations) => {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqLists.map((faq) => {
          const question = translations[faq.heading];
          const answer = translations[faq.content] || ''; // fallback to empty if undefined

          return {
            '@type': 'Question',
            name: question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: answer,
            },
          };
        }),
      };
      const existingScript = this.dom.getElementById('FAQ_SCHEMA');
      if (existingScript) {
        this.dom.getElementById('FAQ_SCHEMA')?.remove();
      }

      const script = this.dom.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'FAQ_SCHEMA';
      script.text = JSON.stringify(faqSchema);
      this.dom.head.appendChild(script);
    });
  }

  updateSocialCards(): void {
    const titleText = this.translate.instant('misc.meta_title');
    const description = this.translate.instant('misc.meta_desc');
    let url = `${this.base}`;

    if (selectedLang && selectedLang !== 'en') {
      url += `/${selectedLang}`
    }

    const image = `${environment.domain}/assets/images/png/logo.png`;

    // Open Graph meta tags
    this.meta.updateTag({ property: 'og:title', content: titleText });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:width', content: '600' });
    this.meta.updateTag({ property: 'og:image:height', content: '600' });

    // Twitter Card meta tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: titleText });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:url', content: url });
    this.meta.updateTag({ name: 'twitter:site', content: environment.twitterId });
  }

  updateHiddenUrls() {
    const ul = this.dom.getElementById('hidden-langs') as Element;
    if (ul) {
      while (ul.firstChild) {
        ul.removeChild(ul.firstChild);
      }
    }
    for (let lang of allLanguages) {
      const li = this.dom.createElement('li');
      const anchor = this.dom.createElement('a');
      let hrefValue = `${this.base}`;

      if (lang.value !== 'en') {
        hrefValue += `/${lang.value}`
      }

      anchor.href = hrefValue;
      anchor.textContent = `${environment.presaleName} ${lang.name}`;
      li.appendChild(anchor);
      ul.appendChild(li);
    }
  }
}
